import { useResetAtom } from 'jotai/utils';
import { useCallback } from 'react';
import {
    netSuiteBillPaymentApplyListState,
    netSuiteBillPaymentFieldValuesState,
    netSuiteExpenseLinesState,
} from 'shared/states';

import {
    netSuiteBillPaymentBillsFilterTransferState,
    netSuiteBillPaymentCreditListState,
    netSuiteBillPaymentSelectableBillsState,
} from '../../NetSuiteBillPaymentRequestForm/NetSuiteBillPaymentRequestForm.states';
import {
    netSuiteBillFieldValuesState,
    updateNetSuiteBillDataState,
} from '../../NetSuiteBillRequestForm/NetSuiteBillRequestForm.states';
import { netSuiteExpenseReportFieldValuesState } from '../../NetSuiteExpenseReportRequestForm/NetSuiteExpenseReportRequestForm.states';
import {
    netSuiteInvoiceFieldValuesState,
    netSuiteInvoiceItemListState,
} from '../../NetSuiteInvoiceRequestForm/NetSuiteInvoiceRequestForm.states';
import {
    netSuiteJournalEntryFieldValuesState,
    netSuiteJournalEntryItemListState,
} from '../../NetSuiteJournalEntryRequestForm/NetSuiteJournalEntryRequestForm.states';
import {
    netSuitePurchaseOrderFieldValuesState,
    netSuiteSendToSupplierState,
} from '../../NetSuitePurchaseOrderRequestForm/NetSuitePurchaseOrderRequestForm.states';
import { netSuiteVendorFieldValuesState } from '../../NetSuiteVendorRequestForm/NetSuiteVendorRequestForm.states';
import {
    companyIdState,
    netSuiteCustomFieldsState,
    netSuiteExpenseItemsState,
    netSuiteItemLineNumberState,
    netSuiteItemLinesState,
    netSuiteShowErrorsState,
} from '../../resources/states';

const useResetCommonFormState = () => {
    const resetCustomFields = useResetAtom(netSuiteCustomFieldsState);
    const resetShowErrors = useResetAtom(netSuiteShowErrorsState);
    const resetItemLineNumber = useResetAtom(netSuiteItemLineNumberState);

    const reset = useCallback(() => {
        resetCustomFields();
        resetShowErrors();
        resetItemLineNumber();
    }, [resetCustomFields, resetItemLineNumber, resetShowErrors]);

    return reset;
};

export const useResetBillPaymentFormState = () => {
    const resetBillPaymentFieldValues = useResetAtom(netSuiteBillPaymentFieldValuesState);
    const resetBillPaymentApplyList = useResetAtom(netSuiteBillPaymentApplyListState);
    const resetBillPaymentCreditList = useResetAtom(netSuiteBillPaymentCreditListState);
    const resetBillPaymentBillsFilterTransfer = useResetAtom(netSuiteBillPaymentBillsFilterTransferState);
    const resetBillPaymentSelectableBills = useResetAtom(netSuiteBillPaymentSelectableBillsState);

    const resetCommonFormState = useResetCommonFormState();

    const reset = useCallback(() => {
        resetBillPaymentFieldValues();
        resetBillPaymentApplyList();
        resetBillPaymentCreditList();
        resetBillPaymentBillsFilterTransfer();
        resetBillPaymentSelectableBills();
        resetCommonFormState();
    }, [
        resetBillPaymentFieldValues,
        resetBillPaymentApplyList,
        resetBillPaymentCreditList,
        resetBillPaymentBillsFilterTransfer,
        resetBillPaymentSelectableBills,
        resetCommonFormState,
    ]);

    return reset;
};

export const useResetBillFormState = () => {
    const resetBillFieldValues = useResetAtom(netSuiteBillFieldValuesState);
    const resetUpdateNetSuiteBillData = useResetAtom(updateNetSuiteBillDataState);
    const resetExpenseItems = useResetAtom(netSuiteExpenseItemsState);
    const resetItemLines = useResetAtom(netSuiteItemLinesState);

    const resetCommonFormState = useResetCommonFormState();

    const reset = useCallback(() => {
        resetBillFieldValues();
        resetUpdateNetSuiteBillData();
        resetExpenseItems();
        resetItemLines();
        resetCommonFormState();
    }, [resetBillFieldValues, resetUpdateNetSuiteBillData, resetExpenseItems, resetItemLines, resetCommonFormState]);

    return reset;
};

export const useResetExpenseReportFormState = () => {
    const resetCompanyId = useResetAtom(companyIdState);
    const resetExpenseReportFieldValues = useResetAtom(netSuiteExpenseReportFieldValuesState);
    const resetExpenseReportExpenseLines = useResetAtom(netSuiteExpenseLinesState);

    const resetCommonFormState = useResetCommonFormState();

    const reset = useCallback(() => {
        resetCompanyId();
        resetExpenseReportFieldValues();
        resetExpenseReportExpenseLines();
        resetCommonFormState();
    }, [resetCompanyId, resetExpenseReportFieldValues, resetExpenseReportExpenseLines, resetCommonFormState]);

    return reset;
};

export const useResetPurchaseOrderFormState = () => {
    const resetPurchaseOrderFieldValues = useResetAtom(netSuitePurchaseOrderFieldValuesState);
    const resetEmailToSupplierEnabled = useResetAtom(netSuiteSendToSupplierState);
    const resetExpenseItems = useResetAtom(netSuiteExpenseItemsState);
    const resetItemLines = useResetAtom(netSuiteItemLinesState);

    const resetCommonFormState = useResetCommonFormState();

    const reset = useCallback(() => {
        resetPurchaseOrderFieldValues();
        resetEmailToSupplierEnabled();
        resetExpenseItems();
        resetItemLines();
        resetCommonFormState();
    }, [
        resetPurchaseOrderFieldValues,
        resetEmailToSupplierEnabled,
        resetExpenseItems,
        resetItemLines,
        resetCommonFormState,
    ]);

    return reset;
};

export const useResetVendorFormState = () => {
    const resetVendorFieldValues = useResetAtom(netSuiteVendorFieldValuesState);

    const resetCommonFormState = useResetCommonFormState();

    const reset = useCallback(() => {
        resetVendorFieldValues();
        resetCommonFormState();
    }, [resetVendorFieldValues, resetCommonFormState]);

    return reset;
};

export const useResetInvoiceFormState = () => {
    const resetInvoiceFieldValues = useResetAtom(netSuiteInvoiceFieldValuesState);
    const resetInvoiceItemList = useResetAtom(netSuiteInvoiceItemListState);

    const resetCommonFormState = useResetCommonFormState();

    const reset = useCallback(() => {
        resetInvoiceFieldValues();
        resetInvoiceItemList();
        resetCommonFormState();
    }, [resetInvoiceFieldValues, resetInvoiceItemList, resetCommonFormState]);

    return reset;
};

export const useResetJournalEntryFormState = () => {
    const resetJournalEntryFieldValues = useResetAtom(netSuiteJournalEntryFieldValuesState);
    const resetJournalEntryItemList = useResetAtom(netSuiteJournalEntryItemListState);

    const resetCommonFormState = useResetCommonFormState();

    const reset = useCallback(() => {
        resetJournalEntryFieldValues();
        resetJournalEntryItemList();
        resetCommonFormState();
    }, [resetJournalEntryFieldValues, resetJournalEntryItemList, resetCommonFormState]);

    return reset;
};
